// @flow

import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';

import { WebsiteDefaultPage as BaseWebsiteDefaultPage } from 'components/Page';
import { PageType } from 'Constants';
import { OriginationFees } from 'data/global/footer/legal/numbered';
import { useImages } from 'hooks';
import { Color, flex, Layout, maxWidthContainer, topBottomMargin, Width } from 'styles';
import { MarketsHero } from 'components/Content';
import { LogoMarkets } from 'components/Svg';
import { ImageLoader } from 'components/Gui';

const METADATA = {
  title: 'Financial products to simplify finances, faster',
  description: `At Markets Direct, we’re transforming home equity by creating a fast, simple and transparent process to bring you closer to your financial goals. We'll show you how to use home equity to consolidate debt, pay for home improvement or fund your retirement. It's quick and easy. Let’s unlock the potential of your largest asset.`,
};

const PAGE_TYPE = {
  product: PageType.PRODUCT.NONE,
};

const WebsiteDefaultPage = styled(BaseWebsiteDefaultPage)`
  width: 100% !important;
`;

const Wrapper = styled.div`
  width: 100%;
  ${flex()};
  flex-direction: column;
  ${topBottomMargin([Layout.NAV_HEIGHT_MOBILE, 0], [Layout.NAV_HEIGHT_DESKTOP, 0], [Layout.NAV_HEIGHT_DESKTOP, 0])};
`;

const Blurb = styled.div`
  width: 100%;
  background: white;
  padding-bottom: 40px;
  border-bottom: 1px solid ${Color.MARKETS_RED};

  p {
    font-size: 2rem;
  }

  > div {
    padding: 40px 0;
    ${maxWidthContainer(1080)};
    ${flex()};
    flex-direction: column;

    > div.logo {
      align-self: flex-start;
      position: relative;
      z-index: 2;
      background: black;
      padding: 10px;
      ${flex()};
      margin-bottom: 40px;

      svg {
        width: 100px;

        path,
        rect,
        polygon {
          fill: white;
        }
      }
    }

    div.blurb {
      margin-top: 20px;
      ${flex()};
      flex-direction: column;

      @media (min-width: ${Width.BREAK_MD}px) {
        flex-direction: row;
        ${flex('space-between', 'flex-start')};
      }

      > div {
        flex: 0 0 auto;
        width: 100%;
        margin-bottom: 40px;

        @media (min-width: ${Width.BREAK_MD}px) {
          width: 260px;
          margin-right: 40px;
          margin-bottom: 0;
        }
      }
    }
  }
`;

const PageContainer = ({ data }: { data: any }) => {
  const getImage = useImages(data);

  return (
    <WebsiteDefaultPage
      page={PAGE_TYPE}
      metaData={{
        ...METADATA,
        image: getImage('about-v2'),
      }}
      footerLegal={{
        numbered: OriginationFees,
      }}>
      <Wrapper>
        <MarketsHero
          title={data?.content.hero.title}
          blurb="Get to know Markets Direct"
          image={{
            name: 'about-v2',
          }}
          images={data}
        />
        <Blurb>
          <div>
            <div className="logo">{LogoMarkets}</div>
            <p>
              Markets Direct is a new brand founded by a veteran team with years of experience in Banking and the trading floors on
              Chicago Board of Trade. The management felt a dire need for a multi asset platform as regulation has driven the different
              asset class providers to converge on a single platform and ensure better management of investor capital and manage a
              clean business.
            </p>
            <div className="blurb">
              <div>
                <ImageLoader image={getImage('empire')} />
              </div>
              <p>
                {`Markets Direct assists investors and traders to use the multiple asset classes under a cross margined arrangement which offers a great convenience and efficient use of investor capital. Markets Direct offers an innovative and a user friendly Multi Asset platform offering FX, Futures, ETFs, US equities and Crypto currencies.
                 
Markets Direct offers a range of trading platforms from NetDania and CQG. Markets Direct will strive to offer the finest spreads compared to our competition in all asset classes, so the convenience is also packaged with exceptional pricing and strong execution. The platform benefits from partnerships with top industry professionals such as Trading Central which provide real time trading signals with easy execution through our mobile App which is considered the best in the industry.`}
              </p>
            </div>
          </div>
        </Blurb>
      </Wrapper>
    </WebsiteDefaultPage>
  );
};

export default PageContainer;

export const query = graphql`
  query {
    content: contentfulAbout {
      hero {
        title
        node_locale
      }
    }
    imageToWebP: allFile(filter: { relativeDirectory: { eq: "pages/about" }, extension: { nin: ["svg", "gif"] } }) {
      edges {
        node {
          ...ImageToWebP
        }
      }
    }
    otherImage: allFile(filter: { relativeDirectory: { eq: "pages/about" }, extension: { in: ["svg", "gif"] } }) {
      edges {
        node {
          ...OtherImage
        }
      }
    }
  }
`;
